import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { ApiEnvelopeNoContent } from './types/api';
import { GetVehicleRouteEnvelope, GetVehicleRouteResponseVM } from './types/get-vehicle-route';
import { VehicleDailyEcPreviewEnvelope, VehicleDailyEcPreviewResponseVM } from './types/get-vehicle-daily-ec-preview';
import { VehiclePolylineEnvelope, VehiclePolylineItemResponse } from './types/get-vehicle-polyline';
import { DepotMapMarkerResponse, DepotMapMarkersEnvelope } from './types/get-depot-map-markers';
import {
  ChargingHubDetailsResponse,
  ChargingHubDetailsResponseEnvelope,
  ChargingHubResponse,
  ChargingHubResponseEnvelope,
  GetChargingHubDetailsArguments,
} from './types/get-charging-hubs';
import {
  ChargingHubStopPositionResponse,
  ChargingHubStopPositionsResponseEnvelope,
  GetChargingHubStopPositionsArguments,
} from './types/get-charging-hub-stop-positions';
import { UpdateDepotConfigurationArguments } from './types/get-depot-configuration';
import { UpdateSideMenuRequestVM } from './types/update-side-menu';
import { ListBevCandidatesResponse, ListBevCandidatesEnvelope } from './types/get-vehicles';
import { SimpleFleetRoutesEnvelope, SimpleFleetRoutes } from './types/get-simple-fleet';
import {
  ImportedDataChassisDataResponse,
  ImportedDataChassisDataEnvelope,
} from './types/get-imported-data-chassis-data';
import { FleetSummaryResponse, ImportedDataFleetSummaryEnvelope } from './types/get-imported-data-fleet-summary';
import { GetVehicleArguments, GetVehicleResponseVM, GetVehicleEnvelope } from './types/get-vehicle';
import { SolutionHomeOverviewEnvelope, SolutionHomeOverviewResponseVM } from './types/get-solution-home-overview';
import {
  GetVehicleSpecificationOptionsEnvelope,
  GetVehicleSpecificationOptionsArguments,
  GetVehicleSpecificationOptionsResponseVM,
} from './types/get-vehicle-specification-options';
import { GetStopsResponse, GetStopsRequestArguments, GetStopsResponseEnvelope } from './types/get-stops';
import { GetTripsResponse, GetTripsRequestArguments, GetTripsResponseEnvelope } from './types/get-trips';
import { UpdateVehicleSpecificationArguments } from './types/update-vehicle-specification';
import { SolutionMetadataEnvelope, SolutionMetadataVM } from './types/get-solutions';
import { SolutionVM, SolutionEnvelope } from './types/get-solution';
import { UserVM, GetUserEnvelope } from './types/get-user';
import { GetCustomersEnvelope, GetCustomersArguments } from './types/get-customers';
import {
  GetChassisByCustomerNameArguments,
  GetChassisByCustomerNameEnvelope,
  GetChassisByCustomerNameResponse,
} from './types/get-chassis-by-customer-name';
import {
  GetChassisOwnerOptionsEnvelope,
  GetChassisOwnerOptionsRequest,
  GetChassisOwnerOptionsResponse,
} from './types/get-chassis-owner-options';
import {
  ValidateChassisNumbersResponse,
  ValidateChassisNumbersRequest,
  ValidateChassisNumbersEnvelope,
} from './types/validate-chassis';
import { AddChassisToSolutionArguments, CreateSolutionWithChassisArguments } from './types/add-chassis-to-solution';
import {
  GetPtoCalculationArguments,
  GetPtoCalculationEnvelope,
  GetPtoCalculationResponseVM,
} from './types/get-pto-calculation';
import { GetPtoOptionsArguments, GetPtoOptionsEnvelope, GetPtoOptionsResponseVM } from './types/get-pto-options';
import {
  GetBatterySummaryArguments,
  GetBatterySummaryEnvelope,
  GetBatterySummaryResponseVM,
} from './types/get-battery-summary';
import { UpdateVehicleBatterySettingsArguments } from './types/update-vehicle-battery-settings';
import {
  GetEnvironmentalImpactArguments,
  GetEnvironmentalImpactEnvelope,
  EnvironmentalImpactResponseVM,
} from './types/get-environmental-impact';
import { UpdateVehicleEnvironmentalImpactSettingsArguments } from './types/update-vehicle-environmental-impact-settings';
import { GetCountryAlpha2CodesEnvelope } from './types/get-country-alpha2-codes';
import { UpdateVehicleEmissionsArguments } from './types/update-vehicle-emissions';
import { DeleteSolutionArguments } from './types/delete-solution';
import { DeleteVehiclesArguments } from './types/delete-vehicles';
import { UpdateSolutionNameArguments } from './types/update-solution-name';
import { ReleaseNoteVM, ReleaseNotesApiEnvelope } from './types/get-release-notes';
import {
  type ChargingHubDetailsTag,
  type ChargingHubsTag,
  type DepotMapMarkersTag,
  type ImportedDataTag,
  type SolutionHomeOverviewTag,
  type SolutionTag,
  type VehicleEnergyPreviewTag,
  type VehiclePolylineTag,
  type VehiclePolylinesTag,
  type VehicleTag,
  tagTypes,
} from './types/cache-tags';
import getSolutionDerivedTags from './utils/get-solution-derived-tags';
import getVehicleDerivedTags from './utils/get-vehicle-derived-tags';
import toQueryString from './utils/to-query-string';
import { parseVehiclePolylineArtifactUrl, parseVehiclePolylines } from './utils/parse-vehicle-polylines';
import { UpdateVehiclePtoArguments } from '@/app/services/solution/types/update-vehicle-pto';
import getApiAccessToken from '@/features/Auth/utils/get-api-access-token';
import { POLYLINE_ARTIFACT_ORIGIN, SOLUTION_ENDPOINT } from '@/app/config';

const baseQuery = fetchBaseQuery({
  baseUrl: `${SOLUTION_ENDPOINT}/api`,
  prepareHeaders: async (headers) => {
    const accessToken = await getApiAccessToken();
    const xSessionId = sessionStorage.getItem('x_session_id');
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    if (xSessionId) {
      headers.set('x-session-id', xSessionId);
    }
    return headers;
  },
});

const baseQueryRetry = retry(baseQuery, {
  maxRetries: 0,
});

const VEHICLE_POLYLINE_ARTIFACT_FETCH_ATTEMPTS = 2;
const MAP_DATA_UNAVAILABLE_ERROR = 'Map data is temporarily unavailable.';
const POLYLINE_ARTIFACT_ORIGINS = new Set([POLYLINE_ARTIFACT_ORIGIN]);

export const solutionApi = createApi({
  reducerPath: 'solutionApi',
  tagTypes,
  baseQuery: baseQueryRetry,
  endpoints: (build) => ({
    // User and shared reference data
    getUser: build.query<UserVM, undefined>({
      query: () => ({
        url: 'v1/current-user/self',
      }),
      providesTags: () => ['User'],
      transformResponse: (response: GetUserEnvelope) => response.content,
    }),
    updateTos: build.mutation<null, undefined>({
      query: () => ({
        url: 'v1/current-user/terms-of-service',
        method: 'PUT',
      }),
      invalidatesTags: () => ['User'],
    }),
    updateSideMenu: build.mutation<null, UpdateSideMenuRequestVM>({
      query: ({ isSideMenuOpen }) => ({
        url: 'v1/current-user/side-menu',
        method: 'PUT',
        params: { isSideMenuOpen },
      }),
      invalidatesTags: () => ['User'],
    }),
    getCountryAlpha2Codes: build.query<string[], undefined>({
      query: () => ({
        url: 'v1/country-codes/alpha2',
      }),
      transformResponse: (response: GetCountryAlpha2CodesEnvelope) => response.content.codes,
    }),
    getReleaseNotes: build.query<ReleaseNoteVM[], undefined>({
      query: () => ({
        url: 'v1/release-notes',
      }),
      transformResponse: ({ content }: ReleaseNotesApiEnvelope) => content.releases,
    }),

    // Solutions
    getSolutions: build.query<SolutionMetadataVM[], undefined>({
      query: () => ({ url: 'v1/solutions' }),
      providesTags: (solutions): SolutionTag[] => {
        const tags: SolutionTag[] = ['Solutions'];

        if (solutions) {
          for (const solution of solutions) {
            tags.push({ type: 'Solutions' as const, id: solution.id });
          }
        }

        return tags;
      },
      transformResponse: (response: SolutionMetadataEnvelope) => response.content,
    }),
    getSolution: build.query<SolutionVM, number | string>({
      query: (id) => ({ url: `v1/solutions/${id}` }),
      providesTags: (_, __, id) => [{ type: 'Solutions', id }],
      transformResponse: (response: SolutionEnvelope) => response.content,
    }),
    getSolutionHomeOverview: build.query<SolutionHomeOverviewResponseVM, string>({
      query: (solutionId) => ({ url: `v1/solutions/${solutionId}/overview` }),
      providesTags: (_, __, solutionId): SolutionHomeOverviewTag[] => [
        { type: 'SolutionHomeOverview', id: solutionId },
      ],
      transformResponse: (response: SolutionHomeOverviewEnvelope) => response.content,
    }),
    addSolution: build.mutation<SolutionEnvelope, Partial<SolutionVM>>({
      query: (body) => ({ url: 'v1/solutions', method: 'POST', body }),
      invalidatesTags: ['Solutions'],
    }),
    addSolutionWithChassis: build.mutation<SolutionEnvelope, CreateSolutionWithChassisArguments>({
      query: (body) => ({
        url: 'v1/solutions/vehicle-candidates',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Solutions', 'Vehicles'],
    }),
    updateSolution: build.mutation<null, Partial<SolutionVM>>({
      query: ({ id, ...body }) => ({
        url: `v1/solutions/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, __, { id }) => [{ type: 'Solutions', id }],
    }),
    updateSolutionName: build.mutation<ApiEnvelopeNoContent, UpdateSolutionNameArguments>({
      query: ({ solutionId, name }) => ({
        url: `v1/solutions/${solutionId}/name`,
        method: 'PUT',
        body: { name },
      }),
      invalidatesTags: ['Solutions'],
    }),
    deleteSolution: build.mutation<ApiEnvelopeNoContent, DeleteSolutionArguments>({
      query: ({ solutionId }) => ({
        url: `v1/solutions/${solutionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Solutions'],
    }),

    // Import candidates and imported data
    getCustomers: build.query<string[], GetCustomersArguments>({
      query: ({ searchTerm }: { searchTerm: string }) => ({
        url: `v1/customers?${toQueryString({ searchTerm })}`,
      }),
      transformResponse: (response: GetCustomersEnvelope) => response.content.customerNames,
    }),
    getChassisByCustomerName: build.query<GetChassisByCustomerNameResponse, GetChassisByCustomerNameArguments>({
      query: ({ customerName }: { customerName: string }) => ({
        url: `v1/customers/chassis-numbers?${toQueryString({ customerName })}`,
      }),
      transformResponse: (response: GetChassisByCustomerNameEnvelope) => response.content,
    }),
    validateChassis: build.mutation<ValidateChassisNumbersResponse, { body: ValidateChassisNumbersRequest }>({
      query: ({ body }) => ({
        url: `v1/chassis-numbers/validate`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: ValidateChassisNumbersEnvelope) => response.content,
    }),
    getChassisOwnerOptions: build.mutation<GetChassisOwnerOptionsResponse, { body: GetChassisOwnerOptionsRequest }>({
      query: ({ body }) => ({
        url: `v1/chassis-numbers/owner-options`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: GetChassisOwnerOptionsEnvelope) => response.content,
    }),
    getImportedDataFleetSummary: build.query<FleetSummaryResponse, string>({
      query: (solutionId) => ({ url: `v1/solutions/${solutionId}/imported-data/fleet-summary` }),
      providesTags: (_, __, solutionId): ImportedDataTag[] => [{ type: 'ImportedData', id: solutionId }],
      transformResponse: (response: ImportedDataFleetSummaryEnvelope) => response.content,
    }),
    getImportedDataChassisData: build.query<ImportedDataChassisDataResponse, string>({
      query: (solutionId) => ({ url: `v1/solutions/${solutionId}/imported-data/chassis-data` }),
      providesTags: (_, __, solutionId): ImportedDataTag[] => [{ type: 'ImportedData', id: solutionId }],
      transformResponse: (response: ImportedDataChassisDataEnvelope) => response.content,
    }),
    addChassisToSolution: build.mutation<ApiEnvelopeNoContent, AddChassisToSolutionArguments>({
      query: ({ solutionId, ...body }) => ({
        url: `v1/solutions/${solutionId}/vehicles/chassis`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, __, { solutionId }) => [
        { type: 'Solutions', id: solutionId },
        'Vehicles',
        ...getSolutionDerivedTags(solutionId),
      ],
    }),

    // Vehicles and vehicle configuration
    getVehicles: build.query<ListBevCandidatesResponse[], string>({
      query: (solutionId) => ({ url: `v1/solutions/${solutionId}/vehicles` }),
      providesTags: (vehicles): VehicleTag[] => {
        const tags: VehicleTag[] = ['Vehicles'];

        if (vehicles) {
          for (const vehicle of vehicles) {
            tags.push({ type: 'Vehicles' as const, id: vehicle.id });
          }
        }

        return tags;
      },
      transformResponse: (response: ListBevCandidatesEnvelope) => response.content.items,
    }),
    getVehicle: build.query<GetVehicleResponseVM, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }: GetVehicleArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}`,
      }),
      providesTags: (_, __, { vehicleId }): VehicleTag[] => [{ type: 'Vehicles', id: Number(vehicleId) }],
      transformResponse: (response: GetVehicleEnvelope) => response.content,
    }),
    getVehicleRoute: build.query<GetVehicleRouteResponseVM, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }) => ({
        url: `v3/solutions/${solutionId}/vehicles/${vehicleId}/route`,
      }),
      transformResponse: (response: GetVehicleRouteEnvelope) => response.content,
    }),
    getVehiclePolylines: build.query<VehiclePolylineItemResponse[], string>({
      queryFn: async (solutionId, api, extraOptions) => {
        for (let attempt = 1; attempt <= VEHICLE_POLYLINE_ARTIFACT_FETCH_ATTEMPTS; attempt += 1) {
          const artifactUrlResult = await baseQueryRetry(
            { url: `v1/solutions/${solutionId}/vehicles/polylines` },
            api,
            extraOptions,
          );

          if ('error' in artifactUrlResult && artifactUrlResult.error) {
            return { error: artifactUrlResult.error, meta: artifactUrlResult.meta };
          }

          try {
            const artifactUrl = parseVehiclePolylineArtifactUrl(artifactUrlResult.data, POLYLINE_ARTIFACT_ORIGIN);
            const parsedArtifactUrl = new URL(artifactUrl);
            const artifactOrigin = parsedArtifactUrl.origin;

            if (POLYLINE_ARTIFACT_ORIGINS.has(artifactOrigin)) {
              const artifactResponse = await fetch(artifactUrl, {
                credentials: 'omit',
                signal: api.signal,
              });

              if (!artifactResponse.ok) {
                throw new Error(`Vehicle polyline artifact request failed with status ${artifactResponse.status}.`);
              }

              return { data: parseVehiclePolylines(await artifactResponse.json()) };
            }

            throw new TypeError('Vehicle polyline artifact response URL has an unexpected origin.');
          } catch (error) {
            if (api.signal.aborted) {
              return { error: { status: 'FETCH_ERROR', error: String(error) } };
            }
          }
        }

        return { error: { status: 'CUSTOM_ERROR', error: MAP_DATA_UNAVAILABLE_ERROR } };
      },
      providesTags: (vehiclePolylines, _, solutionId): (VehiclePolylinesTag | VehiclePolylineTag)[] => [
        { type: 'VehiclePolylines', id: solutionId },
        ...(vehiclePolylines ?? []).map((vehiclePolyline) => ({
          type: 'VehiclePolyline' as const,
          id: vehiclePolyline.vehicleId,
        })),
      ],
    }),
    getVehiclePolyline: build.query<VehiclePolylineItemResponse, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/polyline`,
      }),
      providesTags: (_, __, { vehicleId }): VehiclePolylineTag[] => [
        { type: 'VehiclePolyline', id: Number(vehicleId) },
      ],
      transformResponse: (response: VehiclePolylineEnvelope) => response.content,
    }),
    getVehicleForceFullDailyEc: build.query<VehicleDailyEcPreviewResponseVM, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/force-full-daily-ec`,
        method: 'POST',
      }),
      providesTags: (_, __, { vehicleId }): VehicleEnergyPreviewTag[] => [
        { type: 'VehicleEnergyPreview', id: Number(vehicleId) },
      ],
      transformResponse: (response: VehicleDailyEcPreviewEnvelope) => response.content,
    }),
    getVehicleContinuousDailyEc: build.query<VehicleDailyEcPreviewResponseVM, GetVehicleArguments>({
      query: ({ solutionId, vehicleId }) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/continuous-daily-ec`,
        method: 'POST',
      }),
      providesTags: (_, __, { vehicleId }): VehicleEnergyPreviewTag[] => [
        { type: 'VehicleEnergyPreview', id: Number(vehicleId) },
      ],
      transformResponse: (response: VehicleDailyEcPreviewEnvelope) => response.content,
    }),
    getVehicleSpecificationOptions: build.query<
      GetVehicleSpecificationOptionsResponseVM,
      GetVehicleSpecificationOptionsArguments
    >({
      query: ({ solutionId, ...parameters }: GetVehicleSpecificationOptionsArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/specification-options?${toQueryString(parameters)}`,
      }),
      providesTags: (_, __, arguments_: { vehicleIds?: number[] }): VehicleTag[] => {
        const { vehicleIds } = arguments_;

        if (vehicleIds && vehicleIds.length > 0) {
          const tags: VehicleTag[] = ['Vehicles'];
          for (const id of vehicleIds) {
            tags.push({ type: 'Vehicles', id });
          }
          return tags;
        }

        return ['Vehicles'];
      },
      transformResponse: (response: GetVehicleSpecificationOptionsEnvelope) => response.content,
    }),
    getPtoOptions: build.query<GetPtoOptionsResponseVM, GetPtoOptionsArguments>({
      query: ({ solutionId }) => ({
        url: `v1/solutions/${solutionId}/vehicles/pto-options`,
      }),
      transformResponse: (response: GetPtoOptionsEnvelope) => response.content,
    }),
    calculateVehiclePto: build.query<GetPtoCalculationResponseVM, GetPtoCalculationArguments>({
      query: ({ solutionId, vehicleId, pto }: GetPtoCalculationArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/pto-calculate`,
        method: 'POST',
        body: pto,
      }),
      transformResponse: (response: GetPtoCalculationEnvelope) => response.content,
    }),
    getBatterySummary: build.query<GetBatterySummaryResponseVM, GetBatterySummaryArguments>({
      query: ({ solutionId, vehicleId, ...parameters }: GetBatterySummaryArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/battery-calculation?${toQueryString(parameters)}`,
      }),
      transformResponse: (response: GetBatterySummaryEnvelope) => response.content,
    }),
    getEnvironmentalImpact: build.query<EnvironmentalImpactResponseVM, GetEnvironmentalImpactArguments>({
      query: ({ solutionId, vehicleId }) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/environmental-impact`,
      }),
      transformResponse: (response: GetEnvironmentalImpactEnvelope) => response.content,
    }),
    updateVehicleSpecification: build.mutation<null, UpdateVehicleSpecificationArguments>({
      query: ({ solutionId, updateVehicleSpecificationBody }: UpdateVehicleSpecificationArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/specifications`,
        method: 'PUT',
        body: updateVehicleSpecificationBody,
      }),
      // A No BEV-Match vehicle can enter the BEVs list after specs change.
      invalidatesTags: (_, __, { solutionId, updateVehicleSpecificationBody }) => [
        'Vehicles',
        ...getVehicleDerivedTags(updateVehicleSpecificationBody.vehicleIds),
        ...getSolutionDerivedTags(solutionId),
      ],
    }),
    updateVehiclePto: build.mutation<null, UpdateVehiclePtoArguments>({
      query: ({ solutionId, vehicleIds, pto }: UpdateVehiclePtoArguments) => ({
        url: `v1/solutions/${solutionId}/vehicles/pto`,
        method: 'PUT',
        body: { pto, vehicleIds },
      }),
      invalidatesTags: (_, __, { solutionId, vehicleIds }) => [
        ...getVehicleDerivedTags(vehicleIds),
        ...getSolutionDerivedTags(solutionId),
      ],
    }),
    updateVehicleBatterySettings: build.mutation<ApiEnvelopeNoContent, UpdateVehicleBatterySettingsArguments>({
      query: ({ solutionId, vehicleId, daysOfOperation, yearsOfOwnership }) => ({
        url: `v1/solutions/${solutionId}/vehicles/${vehicleId}/battery-settings`,
        method: 'PUT',
        body: { daysOfOperation, yearsOfOwnership },
      }),
      invalidatesTags: (_, __, { solutionId, vehicleId }) => [
        ...getVehicleDerivedTags([vehicleId]),
        ...getSolutionDerivedTags(solutionId),
      ],
    }),
    updateVehicleEnvironmentalImpactSettings: build.mutation<null, UpdateVehicleEnvironmentalImpactSettingsArguments>({
      query: ({ solutionId, vehicleIds, environmentalImpactSettings }) => ({
        url: `v1/solutions/${solutionId}/vehicles/batch/environmental-impact-settings`,
        method: 'PUT',
        body: { environmentalImpactSettings, vehicleIds },
      }),
      invalidatesTags: (_, __, { solutionId, vehicleIds }) => [
        ...getVehicleDerivedTags(vehicleIds),
        ...getSolutionDerivedTags(solutionId),
      ],
    }),
    updateVehicleEmissions: build.mutation<ApiEnvelopeNoContent, UpdateVehicleEmissionsArguments>({
      query: ({
        solutionId,
        vehicleIds,
        countryAlpha2Code,
        evaluatedChassisConsumptionLPer100Km: consumptionPer100Km,
      }) => ({
        url: `v1/solutions/${solutionId}/vehicles/emissions`,
        method: 'PUT',
        body: { vehicleIds, countryAlpha2Code, evaluatedChassisConsumptionLPer100Km: consumptionPer100Km },
      }),
      invalidatesTags: (_, __, { solutionId, vehicleIds }) => [
        ...getVehicleDerivedTags(vehicleIds),
        ...getSolutionDerivedTags(solutionId),
      ],
    }),
    deleteVehicles: build.mutation<ApiEnvelopeNoContent, DeleteVehiclesArguments>({
      query: ({ solutionId, vehicleIds }) => ({
        url: `v1/solutions/${solutionId}/vehicles`,
        method: 'DELETE',
        body: { vehicleIds },
      }),
      invalidatesTags: (_, __, { solutionId, vehicleIds }) => [
        'Solutions',
        'Vehicles',
        ...getVehicleDerivedTags(vehicleIds),
        ...getSolutionDerivedTags(solutionId),
      ],
    }),

    // Charging hubs and depot configuration
    getChargingHubs: build.query<ChargingHubResponse[], string>({
      query: (solutionId) => ({
        url: `v1/solutions/${solutionId}/charging-hubs`,
      }),
      providesTags: (_, __, solutionId): ChargingHubsTag[] => [{ type: 'ChargingHubs', id: solutionId }],
      transformResponse: (response: ChargingHubResponseEnvelope) => response.content.items,
    }),
    getChargingHubDetails: build.query<ChargingHubDetailsResponse, GetChargingHubDetailsArguments>({
      query: ({ solutionId, locationId }) => ({
        url: `v1/solutions/${solutionId}/charging-hubs/${locationId}`,
      }),
      providesTags: (_, __, { solutionId }): ChargingHubDetailsTag[] => [
        { type: 'ChargingHubDetails', id: solutionId },
      ],
      transformResponse: (response: ChargingHubDetailsResponseEnvelope) => response.content,
    }),
    getChargingHubStopPositions: build.query<ChargingHubStopPositionResponse[], GetChargingHubStopPositionsArguments>({
      query: ({ solutionId, locationId }) => ({
        url: `v1/solutions/${solutionId}/charging-hubs/${locationId}/stop-positions`,
      }),
      transformResponse: (response: ChargingHubStopPositionsResponseEnvelope) => response.content.positions,
    }),
    getDepotMapMarkers: build.query<DepotMapMarkerResponse[], string>({
      query: (solutionId) => ({
        url: `v1/solutions/${solutionId}/depots/map-markers`,
      }),
      providesTags: (_, __, solutionId): DepotMapMarkersTag[] => [{ type: 'DepotMapMarkers', id: solutionId }],
      transformResponse: (response: DepotMapMarkersEnvelope) => response.content.items,
    }),
    updateDepotConfiguration: build.mutation<ApiEnvelopeNoContent, UpdateDepotConfigurationArguments>({
      query: ({ solutionId, locationIds, depotConfiguration }) => ({
        url: `v1/solutions/${solutionId}/charging-hubs/configuration`,
        method: 'PUT',
        body: { locationIds: locationIds.map(Number), ...depotConfiguration },
      }),
      invalidatesTags: (_, __, { solutionId }) => getSolutionDerivedTags(solutionId),
    }),

    // Map and historical stop data
    getFleetRoutes: build.query<SimpleFleetRoutes, string>({
      query: (solutionId) => ({ url: `v3/solutions/${solutionId}/fleet-routes` }),
      transformResponse: (response: SimpleFleetRoutesEnvelope) => response.content,
    }),
    getStops: build.query<GetStopsResponse, GetStopsRequestArguments>({
      query: ({ solutionId, ...body }: GetStopsRequestArguments) => ({
        url: `v1/solutions/${solutionId}/stops`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: GetStopsResponseEnvelope) => response.content,
    }),
    getTrips: build.query<GetTripsResponse, GetTripsRequestArguments>({
      query: ({ solutionId, ...body }: GetTripsRequestArguments) => ({
        url: `v1/solutions/${solutionId}/trips`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: GetTripsResponseEnvelope) => response.content,
    }),
  }),
});

export const {
  useGetUserQuery,
  useUpdateTosMutation,
  useUpdateSideMenuMutation,
  useGetCountryAlpha2CodesQuery,
  useGetReleaseNotesQuery,
  useGetSolutionsQuery,
  useGetSolutionQuery,
  useGetSolutionHomeOverviewQuery,
  useAddSolutionMutation,
  useAddSolutionWithChassisMutation,
  useUpdateSolutionMutation,
  useUpdateSolutionNameMutation,
  useDeleteSolutionMutation,
  useLazyGetCustomersQuery,
  useLazyGetChassisByCustomerNameQuery,
  useValidateChassisMutation,
  useGetChassisOwnerOptionsMutation,
  useGetImportedDataFleetSummaryQuery,
  useGetImportedDataChassisDataQuery,
  useAddChassisToSolutionMutation,
  useGetVehiclesQuery,
  useGetVehicleQuery,
  useGetVehicleRouteQuery,
  useGetVehiclePolylinesQuery,
  useGetVehiclePolylineQuery,
  useGetVehicleForceFullDailyEcQuery,
  useGetVehicleContinuousDailyEcQuery,
  useLazyGetVehicleSpecificationOptionsQuery,
  useGetPtoOptionsQuery,
  useLazyCalculateVehiclePtoQuery,
  useLazyGetBatterySummaryQuery,
  useGetEnvironmentalImpactQuery,
  useUpdateVehicleSpecificationMutation,
  useUpdateVehiclePtoMutation,
  useUpdateVehicleBatterySettingsMutation,
  useUpdateVehicleEnvironmentalImpactSettingsMutation,
  useUpdateVehicleEmissionsMutation,
  useDeleteVehiclesMutation,
  useGetChargingHubsQuery,
  useGetChargingHubDetailsQuery,
  useGetChargingHubStopPositionsQuery,
  useGetDepotMapMarkersQuery,
  useUpdateDepotConfigurationMutation,
  useGetFleetRoutesQuery,
  useGetStopsQuery,
  useGetTripsQuery,
} = solutionApi;
